import { cloneElement } from 'react';
import type { ReactElement } from 'react';
import { FileCheck, Globe2, Calculator, ClipboardCheck } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import ValidationBand from './marketing/ValidationBand';

interface Solution {
  icon: ReactElement<{ className?: string }>;
  titleKey: string;
  descriptionKey: string;
  featureKeys: string[];
}

const solutions: Solution[] = [
  {
    icon: <FileCheck />,
    titleKey: 'landing.solutions.tsrs.title',
    descriptionKey: 'landing.solutions.tsrs.description',
    featureKeys: [
      'landing.solutions.tsrs.feature1',
      'landing.solutions.tsrs.feature2',
      'landing.solutions.tsrs.feature3',
    ],
  },
  {
    icon: <Globe2 />,
    titleKey: 'landing.solutions.cbam.title',
    descriptionKey: 'landing.solutions.cbam.description',
    featureKeys: [
      'landing.solutions.cbam.feature1',
      'landing.solutions.cbam.feature2',
      'landing.solutions.cbam.feature3',
    ],
  },
  {
    icon: <Calculator />,
    titleKey: 'landing.solutions.modules.title',
    descriptionKey: 'landing.solutions.modules.description',
    featureKeys: [
      'landing.solutions.modules.feature1',
      'landing.solutions.modules.feature2',
      'landing.solutions.modules.feature3',
    ],
  },
  {
    icon: <ClipboardCheck />,
    titleKey: 'landing.solutions.evaluation.title',
    descriptionKey: 'landing.solutions.evaluation.description',
    featureKeys: [
      'landing.solutions.evaluation.feature1',
      'landing.solutions.evaluation.feature2',
      'landing.solutions.evaluation.feature3',
    ],
  },
];

export default function Solutions() {
  const { t } = useLanguage();

  return (
    <section className="py-24 bg-gradient-to-br from-emerald-50/30 via-white to-green-50/20" id="solutions">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500/10 to-green-500/10 border border-emerald-200/50 backdrop-blur-sm mb-6">
            <span className="text-sm font-medium text-emerald-700">{t('landing.solutions.eyebrow')}</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t('landing.solutions.title')}
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            {t('landing.solutions.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {solutions.map((solution) => (
            <div
              key={solution.titleKey}
              className="group relative bg-white/80 backdrop-blur-sm rounded-3xl border border-slate-200/50 hover:border-emerald-200/60 overflow-hidden transition-all duration-300 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 p-8"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-green-600 rounded-2xl flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/25 group-hover:scale-110 transition-transform duration-300">
                {cloneElement(solution.icon, { className: 'h-8 w-8 text-white' })}
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-3 group-hover:text-emerald-700 transition-colors">
                {t(solution.titleKey)}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">{t(solution.descriptionKey)}</p>

              <ul className="space-y-3">
                {solution.featureKeys.map((featureKey) => (
                  <li key={featureKey} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-gradient-to-br from-emerald-100 to-green-100 flex items-center justify-center mt-0.5">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                    </div>
                    <span className="text-sm text-slate-600 leading-relaxed">{t(featureKey)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      <ValidationBand />
    </section>
  );
}
